import { hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import {
  ContentStatus,
  ExploreBlock,
  ExploreBlockType,
  ExploreCollection,
  ExploreCollectionDetails,
  GetExploreCollectionsBlocks200Response,
  UpsertExploreBlockRequest,
  UpsertExploreCollectionRequest,
} from '@addmile/backend-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const contentKeys = {
  all: ['content'] as const,
  blocks: () => [...contentKeys.all, 'blocks'] as const,
  block: (slug: string) => [...contentKeys.blocks(), slug] as const,
  blocksList: (params: GetBlocksParams) => [...contentKeys.blocks(), 'list', params] as const,
  collections: () => [...contentKeys.all, 'collections'] as const,
  collectionsList: (params: GetCollectionsParams) =>
    [...contentKeys.collections(), 'list', params] as const,
  allCollections: () => [...contentKeys.collections(), 'all'] as const,
  collectionsPages: (params: GetCollectionsParams) =>
    [...contentKeys.collections(), 'pages', params] as const,
  collection: (slug: string) => [...contentKeys.collections(), slug] as const,
  collectionsBlocks: (blockSlug?: string) =>
    [...contentKeys.all, 'collections-blocks', blockSlug] as const,
};

export interface GetBlocksParams {
  cursor?: number;
  title?: string;
  type?: ExploreBlockType[];
  language?: string;
  status?: ContentStatus[];
  pageSize?: number;
}

export interface BlocksResponse {
  blocks: ExploreBlock[];
  hasMore: boolean;
  cursor?: number;
}

export interface GetCollectionsParams {
  cursor?: number;
  title?: string;
  language?: string;
  status?: ContentStatus[];
  pageSize?: number;
}

export interface CollectionsResponse {
  collections: ExploreCollection[];
  hasMore: boolean;
}

export class ContentManagerService {
  static async getBlock(slug: string): Promise<ExploreBlock> {
    try {
      const token = await getAuthToken();
      const { data } = await hubApi.getExploreBlock(
        encodeURIComponent(slug),
        withAuthHeader(token),
      );
      return data;
    } catch (error) {
      console.error('Error fetching block:', error);
      throw error;
    }
  }

  static async getBlocks(params: GetBlocksParams): Promise<BlocksResponse> {
    const token = await getAuthToken();
    const { data } = await hubApi.getExploreBlocks(
      params.title,
      params.type,
      params.status,
      params.language,
      params.cursor,
      params.pageSize,
      withAuthHeader(token),
    );
    return data;
  }

  static async upsertBlock(
    slug: string,
    blockData: UpsertExploreBlockRequest,
  ): Promise<{ slug: string }> {
    const token = await getAuthToken();
    const { data } = await hubApi.upsertExploreBlock(
      encodeURIComponent(slug),
      blockData,
      withAuthHeader(token),
    );
    return data;
  }

  static async deleteBlock(slug: string): Promise<void> {
    const token = await getAuthToken();
    await hubApi.deleteExploreBlock(encodeURIComponent(slug), withAuthHeader(token));
  }

  static async copyBlock(slug: string, newSlug: string): Promise<{ slug: string }> {
    const token = await getAuthToken();
    const { data } = await hubApi.copyExploreBlock(
      slug,
      { newSlug: newSlug },
      withAuthHeader(token),
    );
    return data;
  }

  static async getCollections(params: GetCollectionsParams): Promise<CollectionsResponse> {
    const token = await getAuthToken();
    const { data } = await hubApi.getExploreCollectionsHub(
      params.title,
      params.status,
      params.language,
      params.cursor,
      params.pageSize,
      withAuthHeader(token),
    );
    return data as unknown as CollectionsResponse;
  }

  // The API returns one page at a time, so follow the id cursor until the last page.
  static async getAllCollections(): Promise<ExploreCollection[]> {
    const collections: ExploreCollection[] = [];
    let cursor: number | undefined;
    let hasMore = true;

    while (hasMore) {
      const page = await ContentManagerService.getCollections({ cursor });
      collections.push(...page.collections);
      cursor = page.collections[page.collections.length - 1]?.id;
      hasMore = page.hasMore && cursor !== undefined;
    }

    return collections;
  }

  static async getCollectionDetails(
    slug: string,
    cursor?: number,
    pageSize?: number,
  ): Promise<ExploreCollectionDetails> {
    const token = await getAuthToken();
    const { data } = await hubApi.getExploreCollectionDetailsV3(
      slug,
      [
        ExploreBlockType.Action,
        ExploreBlockType.Video,
        ExploreBlockType.Article,
        ExploreBlockType.Practice,
        ExploreBlockType.Reflect,
        ExploreBlockType.Quiz,
        ExploreBlockType.MythBuster,
        ExploreBlockType.MissingPiece,
        ExploreBlockType.MatchUp,
        ExploreBlockType.RightSteps,
        ExploreBlockType.YourCall,
      ],
      [ContentStatus.Online, ContentStatus.Draft, ContentStatus.Offline],
      cursor,
      pageSize,
      withAuthHeader(token),
    );
    return data;
  }

  // The blocks are paged by position, so follow the highest position until the last page.
  static async getCollectionDetailsWithAllBlocks(slug: string): Promise<ExploreCollectionDetails> {
    const details = await ContentManagerService.getCollectionDetails(slug);
    const items = [...details.blocks.items];
    let hasMore = details.blocks.hasMore;

    while (hasMore && items.length > 0) {
      const cursor = Math.max(...items.map((block) => block.position));
      const page = await ContentManagerService.getCollectionDetails(slug, cursor);
      items.push(...page.blocks.items);
      hasMore = page.blocks.hasMore && page.blocks.items.length > 0;
    }

    return { ...details, blocks: { items, hasMore: false } };
  }

  static async upsertCollection(
    slug: string,
    data: UpsertExploreCollectionRequest,
  ): Promise<{ slug: string }> {
    const token = await getAuthToken();
    const { data: resp } = await hubApi.upsertExploreCollection(
      encodeURIComponent(slug),
      data,
      withAuthHeader(token),
    );
    return resp as unknown as { slug: string };
  }

  static async deleteCollection(slug: string): Promise<void> {
    const token = await getAuthToken();
    await hubApi.deleteExploreCollection(encodeURIComponent(slug), withAuthHeader(token));
  }

  static async bindCollectionBlock(collectionSlug: string, blockSlug: string): Promise<void> {
    const token = await getAuthToken();
    await hubApi.bindExploreCollectionBlock(collectionSlug, blockSlug, withAuthHeader(token));
  }

  static async updateCollectionBlockPosition(
    collectionSlug: string,
    blockSlug: string,
    position: number,
  ): Promise<void> {
    const token = await getAuthToken();
    await hubApi.updateExploreCollectionBlockPosition(
      collectionSlug,
      blockSlug,
      { position },
      withAuthHeader(token),
    );
  }

  static async unbindCollectionBlock(collectionSlug: string, blockSlug: string): Promise<void> {
    const token = await getAuthToken();
    await hubApi.unbindExploreCollectionBlock(collectionSlug, blockSlug, withAuthHeader(token));
  }

  static async getCollectionsBlocks(
    blockSlug?: string,
    collectionSlug?: string,
    cursor?: number,
    pageSize?: number,
  ): Promise<GetExploreCollectionsBlocks200Response> {
    const token = await getAuthToken();
    const { data } = await hubApi.getExploreCollectionsBlocks(
      collectionSlug,
      blockSlug,
      cursor,
      pageSize,
      withAuthHeader(token),
    );
    return data;
  }
}

export const useContentQueries = () => {
  const queryClient = useQueryClient();

  const useBlock = (slug: string, enabled = true) => {
    return useQuery({
      queryKey: contentKeys.block(slug),
      queryFn: () => ContentManagerService.getBlock(slug),
      enabled: enabled && !!slug,
      staleTime: 5 * 60 * 1000,
    });
  };

  const useBlocks = (params: GetBlocksParams) => {
    return useQuery({
      queryKey: contentKeys.blocksList(params),
      queryFn: () => ContentManagerService.getBlocks(params),
      staleTime: 2 * 60 * 1000,
    });
  };

  const useCollections = (params: GetCollectionsParams) => {
    return useQuery({
      queryKey: contentKeys.collectionsList(params),
      queryFn: () => ContentManagerService.getCollections(params),
      staleTime: 2 * 60 * 1000,
    });
  };

  const useAllCollections = () => {
    return useQuery({
      queryKey: contentKeys.allCollections(),
      queryFn: () => ContentManagerService.getAllCollections(),
      staleTime: 2 * 60 * 1000,
    });
  };

  const useCollectionDetails = (slug: string, cursor?: number, pageSize?: number) => {
    return useQuery({
      queryKey: [...contentKeys.collection(slug), { cursor, pageSize }],
      queryFn: () => ContentManagerService.getCollectionDetails(slug, cursor, pageSize),
      enabled: !!slug,
      staleTime: 2 * 60 * 1000,
    });
  };

  const useCollectionDetailsWithAllBlocks = (slug: string) => {
    return useQuery({
      queryKey: [...contentKeys.collection(slug), 'all-blocks'],
      queryFn: () => ContentManagerService.getCollectionDetailsWithAllBlocks(slug),
      enabled: !!slug,
      staleTime: 2 * 60 * 1000,
    });
  };

  const useUpsertBlock = () => {
    return useMutation({
      mutationFn: ({ slug, data }: { slug: string; data: UpsertExploreBlockRequest }) =>
        ContentManagerService.upsertBlock(slug, data),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: contentKeys.blocks() });
        queryClient.invalidateQueries({ queryKey: contentKeys.block(variables.slug) });
        // Collection views list block titles and statuses, so they go stale on a block save too.
        queryClient.invalidateQueries({ queryKey: contentKeys.collections() });
      },
    });
  };

  const useUpsertCollection = () => {
    return useMutation({
      mutationFn: ({ slug, data }: { slug: string; data: UpsertExploreCollectionRequest }) =>
        ContentManagerService.upsertCollection(slug, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: contentKeys.collections() });
      },
    });
  };

  const useDeleteCollection = () => {
    return useMutation({
      mutationFn: (slug: string) => ContentManagerService.deleteCollection(slug),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: contentKeys.collections() });
      },
    });
  };

  const useBindCollectionBlock = () => {
    return useMutation({
      mutationFn: ({
        collectionSlug,
        blockSlug,
      }: {
        collectionSlug: string;
        blockSlug: string;
        position?: number;
      }) => ContentManagerService.bindCollectionBlock(collectionSlug, blockSlug),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: contentKeys.collectionsBlocks(variables.blockSlug),
        });
        queryClient.invalidateQueries({
          queryKey: contentKeys.collection(variables.collectionSlug),
        });
      },
    });
  };

  const useUnbindCollectionBlock = () => {
    return useMutation({
      mutationFn: ({ collectionSlug, blockSlug }: { collectionSlug: string; blockSlug: string }) =>
        ContentManagerService.unbindCollectionBlock(collectionSlug, blockSlug),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: contentKeys.collectionsBlocks(variables.blockSlug),
        });
        queryClient.invalidateQueries({
          queryKey: contentKeys.collection(variables.collectionSlug),
        });
      },
    });
  };

  const useUpdateCollectionBlockPosition = () => {
    return useMutation({
      mutationFn: ({
        collectionSlug,
        blockSlug,
        position,
      }: {
        collectionSlug: string;
        blockSlug: string;
        position: number;
      }) =>
        ContentManagerService.updateCollectionBlockPosition(collectionSlug, blockSlug, position),
    });
  };

  const useCollectionsBlocks = (blockSlug?: string, enabled = true) => {
    return useQuery({
      queryKey: contentKeys.collectionsBlocks(blockSlug),
      queryFn: () => ContentManagerService.getCollectionsBlocks(blockSlug),
      enabled: enabled && !!blockSlug,
      staleTime: 2 * 60 * 1000,
    });
  };

  const useDeleteBlock = () => {
    return useMutation({
      mutationFn: (slug: string) => ContentManagerService.deleteBlock(slug),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: contentKeys.blocks() });
      },
    });
  };

  const useCopyBlock = () => {
    return useMutation({
      mutationFn: ({ slug, newSlug }: { slug: string; newSlug: string }) =>
        ContentManagerService.copyBlock(slug, newSlug),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: contentKeys.blocks() });
      },
    });
  };

  return {
    useBlock,
    useBlocks,
    useCollections,
    useAllCollections,
    useCollectionDetails,
    useCollectionDetailsWithAllBlocks,
    useUpsertBlock,
    useUpsertCollection,
    useBindCollectionBlock,
    useUnbindCollectionBlock,
    useUpdateCollectionBlockPosition,
    useCollectionsBlocks,
    useDeleteBlock,
    useDeleteCollection,
    useCopyBlock,
  };
};
